import { get, post } from '@/utils/request'

/**
 * 商品相关 API
 */
/**
 * @typedef {Object} PaymentMethod - 支付方式
 * @property {string} pay_method - 支付方式：
 *   - `"1"` = Google Play
 *   - `"2"` = Apple Pay
 *   - `"3"` = 三方支付
 * @property {string} pay_func - 支付渠道：
 *   - `"0"` = Google
 *   - `"1"` = Apple
 *   - `"8"` = PIX（巴西）
 *   - `"10"` = PayPal
 *   - `"11"` = Stripe
 *   - `"12"` = Visa/MasterCard
 *   - ...（更多见文档）
 * @property {string} name - 支付方式显示名称（多语言，如 "Pix"、"PayPal"）
 * @property {string} icon_url - 支付方式图标URL
 * @property {number} sort - 排序权重（越小越靠前）
 * @property {string} [image] - 支付方式图标URL（别名，兼容旧字段）
 * @property {string} [estimate_amount] - 预估金额（某些场景返回，如 "USD1.00"）
 */

/**
 * @typedef {Object} VipRight - VIP权益
 * @property {string} title - 权益标题
 * @property {string} content - 权益描述
 * @property {string} image_url - 权益图标URL
 * @property {string} right_type - 权益类型（once: 一次性, daily: 每日）
 * @property {number} [total_count] - 权益总数量（可选）
 */

/**
 * @typedef {Object} Product - 商品信息
 * @property {string} uuid - 商品唯一标识（用于下单）
 * @property {string} sku - 商品SKU（如："com.iwee.coin.100"）
 * @property {string} name - 商品名称（多语言）
 * @property {number} price - 商品价格（本地货币，如：0.99）
 * @property {number} usd_price - 美元价格（仅用于服务端计算）
 * @property {string} currency - 货币代码（如："USD"、"BRL"、"INR"）
 * @property {number} count - 商品数量（钻石数量或VIP天数）
 * @property {string} desc - 商品描述（多语言）
 * @property {number} discount - 折扣百分比（如：20 表示8折）
 * @property {number} original_price - 原价
 * @property {boolean} is_discount - 是否有折扣
 * @property {string} tag - 商品标签（如："Hot"、"Best"）
 * @property {string} icon_url - 商品图标URL
 * @property {number} order - 排序序号
 * @property {string} [id] - 商品ID（兼容旧字段，同uuid）
 * @property {number} [sku_type] - 商品类型（兼容旧字段）：1=VIP, 3=钻石
 * @property {number} [sku_count] - 商品数量（兼容旧字段，同count）
 * @property {string} [payment_dollar] - 实际支付金额（兼容旧字段，如："1.0000"）
 * @property {string} [currency_price] - 带货币符号的价格（兼容旧字段，如："US$1.00"）
 * @property {string} [currency_origin_price] - 带货币符号的原价（兼容旧字段）
 * @property {string} [limit_desc] - 限购描述（某些场景返回）
 * @property {string} [renewal_desc] - 续费描述（某些场景返回）
 * @property {PaymentMethod[]} [pay_funcs] - 该商品支持的支付方式列表（某些场景返回）
 * @property {VipRight[]} [vip_rights] - VIP权益列表（仅VIP商品有）
 */

/**
 * @typedef {Object} ConfigFunc - 支付配置（在响应根级别）
 * @property {string} config_method - 配置的支付渠道
 * @property {string} config_func - 配置的支付功能
 */

/**
 * @typedef {Object} ProductListResponse - 商品列表响应
 * @property {string} id - 商品列表ID（固定为 "1"）
 * @property {string} category - 商品分类（如："default_products"）
 * @property {boolean} first_pay - 是否为首充用户
 * @property {Product[]} products - 商品列表
 * @property {number} coin_count - 用户当前钻石余额
 * @property {number} bean_count - 用户当前金豆余额
 * @property {boolean} is_subs - 是否为订阅商品
 * @property {PaymentMethod[]} pay_funcs - 可用支付方式列表
 * @property {boolean} can_use_custom_pay - 是否可以使用三方支付
 * @property {boolean} can_use_google_custom_pay - 是否支持 Google 三方支付
 * @property {string} pay_retain_ab - 支付留存AB实验标识
 * @property {CoinSeller[]} coin_seller_list - 钻石卖家列表
 * @property {string} action - 当前action（透传）
 * @property {string} next_action - 下一步操作
 * @property {string} before_action - 二次弹窗的下一步操作
 * @property {string} last_pay_func - 用户最后一次成功支付方式
 * @property {string} default_choose_product_id - 默认选中的商品UUID
 * @property {string} discount_desc - 折扣描述文案
 * @property {boolean} show_diff_price - 是否展示差价（已废弃）
 * @property {string} new_vip_pop_style - 新VIP弹窗样式（固定为 "true"）
 * @property {boolean} new_first_pay_style - 新首充样式（固定为 true）
 * @property {boolean} is_first_pay_vip_ab - 是否为首充提价实验组（固定为 true）
 * @property {string} checkout_page_ab - 结算页AB（已废弃）
 * @property {ConfigFunc[]} [config_funcs] - 可用的支付配置列表（旧字段，兼容）
 * @property {string} [avatar] - 用户头像URL（某些场景返回）
 * @property {number} [member_id] - 用户ID（某些场景返回）
 * @property {string} [nickname] - 用户昵称（某些场景返回）
 * @property {boolean} [is_show_gesture] - 是否显示手势（某些场景返回）
 */

/**
 * @typedef {Object} CoinSeller - 钻石卖家
 * @property {string} id - 卖家加密ID
 * @property {number} member_id - 卖家用户ID
 * @property {string} nickname - 卖家昵称
 * @property {string} avatar - 卖家头像URL
 * @property {string} desc - 引导文案
 */

/**
 * 获取商品列表（VIP套餐、钻石充值或语音房礼包商品）
 *
 * @param {Object} [params] - 查询参数
 * @param {number} [params.sku_type=3] - 商品类型
 * @param {string} [params.category='default'] - 商品分类
 * @param {string} [params.page_name] - 所处页面名称（用于埋点）
 * @param {string} [params.action] - 用户触发的行为（用于弹窗控制）
 * @param {string} [params.before_action] - 二次弹窗透传字段
 * @param {string} [params.scene] - 场景标识（用于埋点）
 *
 * @returns {Promise<ProductListResponse>} 返回商品列表数据
 */
export function getProductList(params = {}, config) {
  return get('/products/v1/list', params, config)
}
Object.assign(getProductList, {
  SKU_TYPE: {
    VIP: 1, // VIP充值
    DIAMOND: 3, // 钻石充值
    VOICE_ROOM_BAG: 4, // 语音房礼包
  },
  CATEGORY: {
    DEFAULT: 'default', // 默认商品列表
    FIRST_PAY: 'first_pay', // 首充商品
    VIP_RECHARGE: 'vip_recharge', // VIP续费
    DISCOUNT: 'discount', // 折扣商品
  },
  ACTION: {
    SEND_MSG_NOT_ENOUGH: 'send_msg_not_enough', // 发送消息金币不足
    RENEW_IN_VIDEO: 'renew_in_video', // 视频内续费
    PRESS_COIN: 'press_coin', // 视频通话内点击金币按钮
    VIDEO_NOT_ENOUGH: 'video_not_enough', // 视频中断，因为金币不足
    MATCH_NOT_ENOUGH: 'match_not_enough', // 匹配金币不足
    // ...
  },
})

/**
 * 获取钻石商品列表
 * @param {Object} [params] - 查询参数
 * @param {string} [params.category='default'] - 商品分类
 * @param {string} [params.page_name] - 所处页面名称（用于埋点）
 * @param {string} [params.action] - 用户触发的行为（用于弹窗控制）
 * @param {string} [params.before_action] - 二次弹窗透传字段
 * @param {string} [params.scene] - 场景标识（用于埋点）
 * @returns {Promise<ProductListResponse>} 返回钻石商品列表数据
 */
export function getDiamondList(params = {}, config) {
  return getProductList({
    sku_type: getProductList.SKU_TYPE.DIAMOND,
    ...params,
    category: getProductList.CATEGORY.DEFAULT,
  }, config)
}

/**
 * 获取 VIP 套餐列表
 * @param {Object} [params] - 查询参数
 * @param {string} [params.category='default'] - 商品分类
 * @param {string} [params.page_name] - 所处页面名称（用于埋点）
 * @param {string} [params.action] - 用户触发的行为（用于弹窗控制）
 * @param {string} [params.before_action] - 二次弹窗透传字段
 * @param {string} [params.scene] - 场景标识（用于埋点）
 * @returns {Promise<ProductListResponse>} 返回 VIP 套餐列表数据
 */
export function getVipList(params = {}, config) {
  return getProductList({
    sku_type: getProductList.SKU_TYPE.VIP,
    ...params,
    category: getProductList.CATEGORY.DEFAULT,
  }, config)
}

/**
 * @typedef {Object} PaymentRequest - 支付请求参数（根据PDF文档）
 * @property {string} config_method - 支付渠道（对应配置项的pay_method）
 * @property {string} config_func - 支付功能（对应配置项的pay_func）
 * @property {string} product_id - 商品ID
 * @property {string} pay_price - 支付价格
 * @property {string} [source] - 支付页面（可选）
 */

/**
 * @typedef {Object} PaymentResponse - 支付响应
 * @property {string} out_trade_no - 订单号
 * @property {string} product_id - 商品ID
 * @property {string} redirect_url - 三方支付跳转地址
 */

/**
 * 创建支付订单（根据PDF文档：使用 /pays/v1/contract_custom_pay 接口）
 * @param {PaymentRequest} data - 支付请求参数
 * @returns {Promise<PaymentResponse>} 返回支付跳转URL等信息
 */
export function createPayment(data) {
  return post('/pays/v1/contract_custom_pay', data)
}
